import React, { useCallback, useState } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box, CameraIcon, DocumentsIcon, FilesIcon, GalleryIcon, RawButton, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { MAX_SIZE_OF_FILE_IN_MEGABYTES } from '@/common/constants';
import FileGetAndUploadDropButton from '@/components/controls/DropScan/FileGetAndUploadButton/FileGetAndUploadDropButton';
import UploadButton from '@/components/controls/DropScan/UploadButton/UploadButton';
import UploadTrigger from '@/components/controls/DropScan/UploadButton/UploadTrigger';
import { GuidelinesButton, GuidelinesPopup } from '@/modules/UseMyMoney/components';

const StyledDocumentsUploadButton = styled(RawButton)`
  &:hover #documents-icon_inner {
    fill: ${({ theme }) => theme.colors.accentActive};
  }
`;

interface Props {
  storeKey: string;
  isMobile?: boolean;
  onUpload?: () => void;
  onFileUploadError?: (error?: FileError) => void;
  disableFilesUpload?: boolean;
  isOcrDisabled?: boolean;
}

const UploadFromDeviceCard: React.FC<Props> = ({
  storeKey,
  isMobile,
  onUpload,
  onFileUploadError,
  disableFilesUpload = false,
  isOcrDisabled,
}) => {
  const { t } = useTranslation();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const handleOpenPopup = useCallback(() => {
    setIsPopupVisible(true);
  }, []);

  if (isMobile) {
    return (
      <Box data-testid="upload-from-device_mobile-wrapper">
        <Box direction="row" justify="between" align="center" margin={{ bottom: 'spacing12' }}>
          <Text weight={700}>
            {t('How do you want to upload?')}
          </Text>
          <GuidelinesButton onClick={handleOpenPopup} isAccent />
        </Box>

        <Box>
          <FileGetAndUploadDropButton
            onUpload={onUpload}
            onFileUploadError={onFileUploadError}
            onLoadingChange={setIsFileLoading}
            isFileLoading={isFileLoading}
            storeKey={storeKey}
            disableFilesUpload={disableFilesUpload}
            ocrRequired={!isOcrDisabled}
            maxFiles={10}
            title={t('Use Camera')}
            id="Camera"
            cameraInput
            accept="image/*"
            icon={<CameraIcon color="canvas" />}
            isRightIconPosition
          />
        </Box>
        <Box margin={{ top: 'spacing12' }}>
          <FileGetAndUploadDropButton
            onUpload={onUpload}
            onFileUploadError={onFileUploadError}
            onLoadingChange={setIsFileLoading}
            isFileLoading={isFileLoading}
            storeKey={storeKey}
            disableFilesUpload={disableFilesUpload}
            ocrRequired={!isOcrDisabled}
            maxFiles={10}
            title={t('Select From Gallery')}
            id="Gallery"
            secondary
            accept="image/*"
            icon={<GalleryIcon color="iconPrimary" />}
            isRightIconPosition
          />
        </Box>
        <Box margin={{ top: 'spacing12' }}>
          <FileGetAndUploadDropButton
            onUpload={onUpload}
            onFileUploadError={onFileUploadError}
            onLoadingChange={setIsFileLoading}
            isFileLoading={isFileLoading}
            storeKey={storeKey}
            disableFilesUpload={disableFilesUpload}
            ocrRequired={!isOcrDisabled}
            maxFiles={10}
            title={t('Browse Files')}
            id="Files"
            secondary
            accept="image/*,application/pdf,.pdf,.heic,.heif"
            icon={<FilesIcon color="iconPrimary" />}
            isRightIconPosition
          />
        </Box>

        {isPopupVisible && <GuidelinesPopup onSetVisible={setIsPopupVisible} />}
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid="upload-from-device_wrapper"
        pad={{ horizontal: 'spacing12', bottom: 'spacing12', top: 'spacing24' }}
        background="module"
        round="container1Round"
        align="center"
      >
        <UploadTrigger
          onUpload={onUpload}
          onFileUploadError={onFileUploadError}
          onLoadingChange={setIsFileLoading}
          storeKey={storeKey}
          disableFilesUpload={disableFilesUpload}
          ocrRequired={!isOcrDisabled}
          multiple
          maxFiles={10}
        >
          {({ open, isLoading }) => (
            <StyledDocumentsUploadButton
              data-testid="upload-from-device_icon-button"
              onClick={open}
              disabled={isLoading || isFileLoading}
            >
              <DocumentsIcon width="150" height="100" />
            </StyledDocumentsUploadButton>
          )}
        </UploadTrigger>
        <Box width={{ max: '245px' }}>
          <Text
            data-testid="upload-from-device_title"
            margin={{ top: 'spacing12' }}
            weight={700}
            textAlign="center"
          >
            {t('The document is on your device? Upload')}
          </Text>
        </Box>

        <Text
          data-testid="upload-from-device_file-types"
          margin={{ top: 'spacing8' }}
          color="textSecondary"
          textAlign="center"
        >
          PDF, HEIF, JPEG…
          <GuidelinesButton onClick={handleOpenPopup} />
        </Text>

        <Text
          data-testid="upload-from-device_max-size"
          margin={{ top: 'spacing2' }}
          color="textSecondary"
          textAlign="center"
        >
          {t('Max. file size:')} {MAX_SIZE_OF_FILE_IN_MEGABYTES}MB
        </Text>

        <Box margin={{ top: 'spacing24' }} width="100%">
          <UploadButton
            testId="upload-from-device"
            onUpload={onUpload}
            onFileUploadError={onFileUploadError}
            onLoadingChange={setIsFileLoading}
            isFileLoading={isFileLoading}
            width="100%"
            storeKey={storeKey}
            disableFilesUpload={disableFilesUpload}
            ocrRequired={!isOcrDisabled}
            multiple
            maxFiles={10}
          >
            {t('Upload Document')}
          </UploadButton>
        </Box>
      </Box>

      {isPopupVisible && <GuidelinesPopup onSetVisible={setIsPopupVisible} />}
    </>
  );
};

export default UploadFromDeviceCard;
